if (!customElements.get('shady-collection-tabs')) {
  class ShadyCollectionTabs extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.resizeObserver = null;
      this.stickyObserver = null;
      this.pendingGrids = new Set();
      this.layoutFrame = null;
      this.activeId = null;
      this.isStuck = false;
      this.switchToken = 0;
      this.lastPanelsWidth = 0;
      this.nativeMasonry = false;
    }

    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.tabList = this.querySelector('[data-tab-list]');
      this.tabs = Array.from(this.querySelectorAll('[data-sct-tab]'));
      this.panelsContainer = this.querySelector('[data-panels]');
      this.stickyBar = this.querySelector('[data-sticky-bar]');
      this.stickySentinel = this.querySelector('[data-sticky-sentinel]');
      this.status = this.querySelector('[data-status]');
      this.stickyOffset = Number.parseFloat(this.dataset.stickyOffset) || 0;
      this.productsPerReveal = Math.max(1, Number.parseInt(this.dataset.productsPerReveal, 10) || 6);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.nativeMasonry = typeof CSS !== 'undefined' && CSS.supports('display', 'grid-lanes');

      if (!this.tabList || !this.tabs.length || !this.panelsContainer) return;

      this.classList.toggle('uses-native-grid-lanes', this.nativeMasonry);
      this.dataset.masonryEngine = this.nativeMasonry ? 'grid-lanes' : 'javascript';
      this.enhanceTabs();
      this.setupObservers();

      this.addEventListener('click', this.handleClick.bind(this), {
        signal: this.abortController.signal
      });
      this.addEventListener('keydown', this.handleKeydown.bind(this), {
        signal: this.abortController.signal
      });
      document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this), {
        signal: this.abortController.signal
      });

      const activeTab = this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0];
      this.activeId = activeTab.dataset.blockId;
      const activePanel = this.ensurePanel(this.activeId);

      if (activePanel) {
        activePanel.hidden = false;
        this.preparePanel(activePanel);
      }

      this.classList.add('is-enhanced');
      this.requestLayout(activePanel?.querySelector('[data-product-grid]'));

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this.requestLayout();
        });
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.stickyObserver?.disconnect();
      this.stickyObserver = null;

      if (this.layoutFrame) {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = null;
      }

      this.pendingGrids.clear();
    }

    enhanceTabs() {
      this.tabList.setAttribute('role', 'tablist');
      this.tabList.setAttribute('aria-orientation', 'horizontal');

      this.tabs.forEach((tab, index) => {
        const blockId = tab.dataset.blockId;
        const panelId = `SctPanel-${this.dataset.sectionId}-${blockId}`;
        const selected = index === 0;

        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panelId);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        tab.classList.toggle('is-active', selected);
      });
    }

    setupObservers() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target === this.panelsContainer) {
              const nextWidth = Math.round(entry.contentRect.width);
              if (nextWidth !== this.lastPanelsWidth) {
                this.lastPanelsWidth = nextWidth;
                this.requestLayout();
              }
              return;
            }

            const grid = entry.target.closest('[data-product-grid]');
            if (grid) this.requestLayout(grid);
          });
        });

        this.resizeObserver.observe(this.panelsContainer);
      } else {
        window.addEventListener('resize', () => this.requestLayout(), {
          passive: true,
          signal: this.abortController.signal
        });
      }

      if ('IntersectionObserver' in window && this.stickySentinel) {
        this.stickyObserver = new IntersectionObserver(
          ([entry]) => {
            const stuck = !entry.isIntersecting && entry.boundingClientRect.top <= this.stickyOffset;
            if (stuck === this.isStuck) return;

            this.isStuck = stuck;
            this.classList.toggle('is-stuck', stuck);

            if (!stuck) this.panelsContainer.style.minBlockSize = '';
          },
          {
            threshold: [0, 1],
            rootMargin: `${-this.stickyOffset}px 0px 0px 0px`
          }
        );

        this.stickyObserver.observe(this.stickySentinel);
      }
    }

    handleClick(event) {
      const seeMoreButton = event.target.closest('[data-see-more]');
      if (seeMoreButton && this.contains(seeMoreButton)) {
        this.revealNextBatch(seeMoreButton, event.detail === 0);
        return;
      }

      const tab = event.target.closest('[data-sct-tab]');
      if (!tab || !this.contains(tab)) return;

      event.preventDefault();
      this.activateTab(tab.dataset.blockId, {
        scrollToPanel: this.isStuck,
        centerTab: true
      });
    }

    handleKeydown(event) {
      const currentTab = event.target.closest('[data-sct-tab]');
      if (!currentTab || !this.contains(currentTab)) return;

      const currentIndex = this.tabs.indexOf(currentTab);
      let nextIndex = null;
      const isRtl = getComputedStyle(this.tabList).direction === 'rtl';

      if (event.key === 'ArrowRight') {
        nextIndex = isRtl ? currentIndex - 1 : currentIndex + 1;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = isRtl ? currentIndex + 1 : currentIndex - 1;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else if (event.key === ' ') {
        event.preventDefault();
        this.activateTab(currentTab.dataset.blockId, {
          scrollToPanel: this.isStuck,
          centerTab: true
        });
        return;
      } else {
        return;
      }

      event.preventDefault();
      nextIndex = (nextIndex + this.tabs.length) % this.tabs.length;
      const nextTab = this.tabs[nextIndex];
      nextTab.focus({ preventScroll: true });
      this.activateTab(nextTab.dataset.blockId, {
        scrollToPanel: this.isStuck,
        centerTab: true
      });
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
      if (!blockId || !this.tabs.some((tab) => tab.dataset.blockId === blockId)) return;

      this.activateTab(blockId, {
        scrollToPanel: false,
        centerTab: true
      });
    }

    ensurePanel(blockId) {
      let panel = this.panelsContainer.querySelector(`[data-panel][data-block-id="${CSS.escape(blockId)}"]`);
      if (panel) return panel;

      const template = this.panelsContainer.querySelector(
        `template[data-panel-template][data-block-id="${CSS.escape(blockId)}"]`
      );
      if (!template) return null;

      const fragment = template.content.cloneNode(true);
      panel = fragment.querySelector('[data-panel]');
      this.panelsContainer.append(fragment);
      template.remove();
      return panel;
    }

    preparePanel(panel) {
      if (!panel || panel.dataset.prepared === 'true') return;

      const blockId = panel.dataset.blockId;
      const tab = this.tabs.find((candidate) => candidate.dataset.blockId === blockId);
      panel.dataset.prepared = 'true';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('tabindex', '0');

      if (tab) panel.setAttribute('aria-labelledby', tab.id);

      const cards = panel.querySelectorAll('[data-product-card]');
      cards.forEach((card) => this.resizeObserver?.observe(card));
      this.syncPanelActions(panel);
    }

    syncPanelActions(panel) {
      const grid = panel.querySelector('[data-product-grid]');
      const remainingProducts = grid?.querySelectorAll('template[data-product-template]').length || 0;
      const shownProducts = Array.from(grid?.children || []).filter((child) =>
        child.matches('[data-product-card]')
      ).length;
      const configuredInlineTotal = Number.parseInt(panel.dataset.renderedCount, 10);
      const configuredCollectionTotal = Number.parseInt(panel.dataset.collectionCount, 10);
      const inlineTotal = Number.isFinite(configuredInlineTotal)
        ? configuredInlineTotal
        : shownProducts + remainingProducts;
      const collectionTotal = Number.isFinite(configuredCollectionTotal)
        ? configuredCollectionTotal
        : inlineTotal;
      const nextCount = Math.min(this.productsPerReveal, remainingProducts);
      const hasMore = remainingProducts > 0;
      const actionGroup = panel.querySelector('[data-action-group]');
      const seeMoreButton = panel.querySelector('[data-see-more]');
      const viewAllLink = panel.querySelector('[data-view-all]');
      const loadMoreLabel = seeMoreButton?.querySelector('[data-load-more-label]');
      const revealProgress = seeMoreButton?.querySelector('[data-reveal-progress]');
      const allProductsLabel = viewAllLink?.querySelector('[data-all-products-label]');

      if (loadMoreLabel) {
        loadMoreLabel.textContent = this.formatCountLabel(
          seeMoreButton.dataset.loadMoreFormat,
          nextCount,
          'Show [count] more'
        );
      }

      if (revealProgress) {
        revealProgress.textContent = `(${shownProducts} of ${inlineTotal} featured)`;
      }

      if (allProductsLabel) {
        allProductsLabel.textContent = this.formatCountLabel(
          viewAllLink.dataset.allProductsFormat,
          collectionTotal,
          'All [count]'
        );
      }

      if (actionGroup) actionGroup.toggleAttribute('data-complete', !hasMore);
      if (seeMoreButton) seeMoreButton.hidden = !hasMore;
      if (viewAllLink) viewAllLink.hidden = false;

      return { collectionTotal, hasMore, inlineTotal, nextCount, shownProducts };
    }

    formatCountLabel(format, count, fallback) {
      const template = format?.trim() || fallback;

      return template.includes('[count]')
        ? template.split('[count]').join(String(count))
        : `${template} ${count}`;
    }

    activateTab(blockId, options = {}) {
      const nextTab = this.tabs.find((tab) => tab.dataset.blockId === blockId);
      if (!nextTab) return;

      const currentPanel = this.panelsContainer.querySelector('[data-panel]:not([hidden])');
      const nextPanel = this.ensurePanel(blockId);
      if (!nextPanel) return;

      const shouldScroll = Boolean(options.scrollToPanel && this.isStuck);
      const switchToken = ++this.switchToken;

      if (currentPanel !== nextPanel) {
        const currentHeight = this.panelsContainer.getBoundingClientRect().height;
        if (currentHeight > 0) this.panelsContainer.style.minBlockSize = `${currentHeight}px`;

        if (currentPanel) currentPanel.hidden = true;
        nextPanel.hidden = false;
        this.activeId = blockId;
      }

      this.tabs.forEach((tab) => {
        const selected = tab === nextTab;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });

      this.preparePanel(nextPanel);
      this.requestLayout(nextPanel.querySelector('[data-product-grid]'));

      if (options.centerTab) this.centerTab(nextTab);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (switchToken !== this.switchToken) return;
          if (shouldScroll) {
            this.reservePanelViewport(nextPanel);
            this.alignPanel(nextPanel);
          } else {
            this.panelsContainer.style.minBlockSize = '';
          }
        });
      });
    }

    centerTab(tab) {
      const listRect = this.tabList.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const delta = tabRect.left - listRect.left - (listRect.width - tabRect.width) / 2;

      this.tabList.scrollBy({
        left: delta,
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
    }

    alignPanel(panel) {
      const stickyHeight = this.stickyBar?.getBoundingClientRect().height || 0;
      const safeTop = this.stickyOffset + stickyHeight + 12;
      const panelTop = window.scrollY + panel.getBoundingClientRect().top;

      window.scrollTo({
        top: Math.max(0, panelTop - safeTop),
        behavior: 'auto'
      });
    }

    reservePanelViewport(panel) {
      const stickyHeight = this.stickyBar?.getBoundingClientRect().height || 0;
      const availableHeight = Math.max(0, window.innerHeight - this.stickyOffset - stickyHeight - 12);
      const panelHeight = panel.getBoundingClientRect().height;
      this.panelsContainer.style.minBlockSize = `${Math.max(panelHeight, availableHeight)}px`;
    }

    revealNextBatch(button, keyboardTriggered) {
      const panel = button.closest('[data-panel]');
      const grid = panel?.querySelector('[data-product-grid]');
      const templates = Array.from(grid?.querySelectorAll('template[data-product-template]') || []).slice(
        0,
        this.productsPerReveal
      );
      if (!panel || !grid || !templates.length) return;

      const viewportAnchor = this.captureViewportAnchor(panel, button);
      const newCards = [];
      templates.forEach((template) => {
        const fragment = template.content.cloneNode(true);
        newCards.push(...fragment.querySelectorAll('[data-product-card]'));
        template.before(fragment);
        template.remove();
      });

      const batchIndex = Math.max(0, Number.parseInt(grid.dataset.masonryBatch, 10) || 0) + 1;
      grid.dataset.masonryBatch = String(batchIndex);
      newCards.forEach((card) => {
        card.dataset.masonryBatch = String(batchIndex);
      });

      const firstDeferredTemplate = grid.querySelector('template[data-product-template]');
      const orderedNewCards = this.sortCardEntries(this.getCardEntries(newCards)).map(({ card }) => card);
      orderedNewCards.forEach((card) => grid.insertBefore(card, firstDeferredTemplate));

      newCards.forEach((card) => this.resizeObserver?.observe(card));
      const actionState = this.syncPanelActions(panel);
      this.requestLayout(grid);
      this.announceProductCount(panel, grid);

      const shouldMoveFocus = keyboardTriggered || actionState.hasMore === false;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (shouldMoveFocus) {
            const firstVisualCard = this.getFirstVisualCard(newCards);
            const firstProductLink = firstVisualCard?.querySelector('.sct__card-link');
            if (firstProductLink) this.focusNewProduct(firstProductLink);
          } else {
            this.restoreViewportAnchor(viewportAnchor);
          }
        });
      });
    }

    captureViewportAnchor(panel, fallback) {
      const stickyHeight = this.isStuck ? this.stickyBar?.getBoundingClientRect().height || 0 : 0;
      const safeTop = this.stickyOffset + stickyHeight + 12;
      const cards = Array.from(panel.querySelectorAll('[data-product-card]'));
      const element =
        cards.find((card) => {
          const rect = card.getBoundingClientRect();
          return rect.bottom > safeTop && rect.top < window.innerHeight;
        }) || fallback;

      if (!element) return null;
      return { element, top: element.getBoundingClientRect().top };
    }

    restoreViewportAnchor(anchor) {
      if (!anchor?.element?.isConnected) return;

      const delta = anchor.element.getBoundingClientRect().top - anchor.top;
      if (Math.abs(delta) < 1) return;
      window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
    }

    getFirstVisualCard(cards) {
      return cards
        .filter((card) => card.isConnected)
        .map((card) => ({ card, rect: card.getBoundingClientRect() }))
        .sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left)[0]?.card;
    }

    announceProductCount(panel, grid) {
      if (!this.status) return;

      const shown = Array.from(grid.children).filter((child) => child.matches('[data-product-card]')).length;
      const template = panel.dataset.statusTemplate || '__shown__ products shown';
      this.status.textContent = '';

      requestAnimationFrame(() => {
        this.status.textContent = template.replace('__shown__', String(shown));
      });
    }

    focusNewProduct(link) {
      link.focus({ preventScroll: true });

      const rect = link.getBoundingClientRect();
      const stickyHeight = this.isStuck ? this.stickyBar?.getBoundingClientRect().height || 0 : 0;
      const safeTop = this.stickyOffset + stickyHeight + 12;
      const safeBottom = window.innerHeight - 16;

      if (rect.top < safeTop || rect.bottom > safeBottom) {
        window.scrollTo({
          top: Math.max(0, window.scrollY + rect.top - safeTop),
          behavior: 'auto'
        });
      }
    }

    getCardEntries(cards) {
      return cards.map((card) => ({
        card,
        height: card.getBoundingClientRect().height,
        sourceIndex: Number.parseInt(card.dataset.productIndex, 10) || 0,
        batchIndex: Number.parseInt(card.dataset.masonryBatch, 10) || 0
      }));
    }

    sortCardEntries(entries) {
      return entries.sort((a, b) => {
        if (a.batchIndex !== b.batchIndex) return a.batchIndex - b.batchIndex;

        const heightDifference = b.height - a.height;

        // Equal grid tracks can differ by a fraction of a pixel. Treating that
        // rounding noise as a meaningful height change makes equally sized cards
        // swap columns forever as ResizeObserver schedules the next layout.
        return Math.abs(heightDifference) < 1 ? a.sourceIndex - b.sourceIndex : heightDifference;
      });
    }

    requestLayout(grid) {
      if (grid) this.pendingGrids.add(grid);

      if (this.layoutFrame) return;
      this.layoutFrame = requestAnimationFrame(() => this.flushLayout());
    }

    flushLayout() {
      this.layoutFrame = null;

      const grids = this.pendingGrids.size
        ? Array.from(this.pendingGrids)
        : Array.from(this.querySelectorAll('[data-panel]:not([hidden]) [data-product-grid]'));
      this.pendingGrids.clear();

      const plans = grids
        .filter((grid) => grid.isConnected && !grid.closest('[data-panel]')?.hidden)
        .map((grid) => {
          const style = getComputedStyle(grid);
          const columns = Math.max(1, Number.parseInt(style.getPropertyValue('--sct-column-count'), 10) || 2);
          const rowHeight = Number.parseFloat(style.gridAutoRows) || 4;
          const rowGap = Number.parseFloat(style.rowGap) || 0;
          const cards = Array.from(grid.children).filter((child) => child.matches('[data-product-card]'));
          const entries = this.getCardEntries(cards);

          if (grid.dataset.masonryReady !== 'true') this.sortCardEntries(entries);

          const columnRows = Array(columns).fill(1);
          let activeBatchIndex = entries[0]?.batchIndex || 0;

          const positions = entries.map(({ height, batchIndex }) => {
            if (batchIndex !== activeBatchIndex) {
              // Begin each revealed batch below the deepest existing column so
              // newly added products cannot be packed above the shopper.
              const previousBatchFrontier = Math.max(...columnRows);
              columnRows.fill(previousBatchFrontier);
              activeBatchIndex = batchIndex;
            }

            let column = 0;
            for (let index = 1; index < columnRows.length; index += 1) {
              if (columnRows[index] < columnRows[column]) column = index;
            }

            const rowStart = columnRows[column];
            const rowSpan = Math.max(1, Math.ceil((height + rowGap) / (rowHeight + rowGap)));
            columnRows[column] = rowStart + rowSpan;
            return { column, rowStart, rowSpan };
          });

          return { grid, entries, positions };
        });

      plans.forEach(({ grid, entries, positions }) => {
        const firstDeferredTemplate = grid.querySelector('template[data-product-template]');
        const currentCards = Array.from(grid.children).filter((child) => child.matches('[data-product-card]'));
        const orderChanged = entries.some(({ card }, index) => currentCards[index] !== card);

        if (orderChanged) {
          entries.forEach(({ card }) => grid.insertBefore(card, firstDeferredTemplate));
        }

        entries.forEach(({ card }, index) => {
          if (this.nativeMasonry) {
            if (card.style.gridColumn) card.style.gridColumn = '';
            if (card.style.gridRow) card.style.gridRow = '';
            return;
          }

          const position = positions[index];
          const nextColumn = String(position.column + 1);
          const nextRow = `${position.rowStart} / span ${position.rowSpan}`;

          if (card.style.gridColumn !== nextColumn) card.style.gridColumn = nextColumn;
          if (card.style.gridRow !== nextRow) card.style.gridRow = nextRow;
        });
        grid.dataset.masonryReady = 'true';
      });
    }
  }

  customElements.define('shady-collection-tabs', ShadyCollectionTabs);
}
